import { forwardRef } from 'react'
import { styled } from '@mui/material'
import Box from '@mui/material/Box'
import Badge from '@mui/material/Badge'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'

import { AvatarDisplay, AvatarProps } from '../AvatarDisplay'

const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  const today = new Date()

  if (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  ) {
    return date.toLocaleTimeString([], { timeStyle: 'short' })
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

type ChannelListItemProps = {
  images: AvatarProps[]
  title: string
  subtitle?: string
  unreadMessageCount: number
  lastMessage?: {
    text: string
    date: string
    seen: boolean
  }
  isArchived?: boolean
  onClick: () => void
}

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    fontFamily: 'Arial, sans-serif',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    lineHeight: 'normal',
    textAlign: 'center',
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    minWidth: 20,
    height: 20,
    borderRadius: 10,
  },
}))

const StyledListItem = styled(ListItem)(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  display: 'flex',
  alignItems: 'flex-start',
  cursor: 'pointer',
  padding: theme.spacing(2),
}))

const ChannelListItem = forwardRef<HTMLLIElement | null, ChannelListItemProps>(
  (
    {
      images,
      title,
      subtitle,
      unreadMessageCount,
      lastMessage,
      isArchived,
      onClick,
    },
    ref
  ) => {
    const hasUnread = unreadMessageCount > 0 && !isArchived

    return (
      <>
        <StyledListItem onClick={onClick} ref={ref}>
          <ListItemAvatar sx={{ minWidth: 56 }}>
            <AvatarDisplay images={images} />
          </ListItemAvatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Row 1: Title + timestamp (for DMs) or just title (for cases) */}
            {subtitle ? (
              <>
                {/* Case layout: title on its own line(s) */}
                <Typography
                  variant="subtitle1"
                  color="text.primary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.3,
                  }}
                >
                  {title}
                </Typography>
                {/* Subtitle + timestamp row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: 0.25,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    sx={{ flexShrink: 1, minWidth: 0 }}
                  >
                    {subtitle}
                  </Typography>
                  {lastMessage?.date && (
                    <Typography
                      variant="body2"
                      sx={{
                        flexShrink: 0,
                        ml: 1,
                        color: hasUnread ? 'secondary.main' : 'text.secondary',
                        fontWeight: hasUnread ? 600 : 400,
                      }}
                    >
                      {formatDate(lastMessage.date)}
                    </Typography>
                  )}
                </Box>
              </>
            ) : (
              /* DM layout: title + timestamp on same row */
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  variant="subtitle1"
                  color="text.primary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.3,
                    flexShrink: 1,
                    minWidth: 0,
                  }}
                >
                  {title}
                </Typography>
                {lastMessage?.date && (
                  <Typography
                    variant="body2"
                    sx={{
                      flexShrink: 0,
                      ml: 1,
                      color: hasUnread ? 'secondary.main' : 'text.secondary',
                      fontWeight: hasUnread ? 600 : 400,
                    }}
                  >
                    {formatDate(lastMessage.date)}
                  </Typography>
                )}
              </Box>
            )}

            {/* Message preview + unread badge */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mt: 0.5,
              }}
            >
              <Typography
                variant="body2"
                color={lastMessage?.text ? 'text.secondary' : 'text.disabled'}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  fontStyle: lastMessage?.text ? 'normal' : 'italic',
                }}
              >
                {lastMessage?.text || 'No messages yet'}
              </Typography>
              {hasUnread && (
                <StyledBadge
                  badgeContent={unreadMessageCount}
                  max={9}
                  sx={{ ml: 2, mr: 1 }}
                />
              )}
            </Box>
          </Box>
        </StyledListItem>
        <Divider />
      </>
    )
  }
)

ChannelListItem.displayName = 'ChannelListItem'

export default ChannelListItem
